<template>
  <div
    ref="rowEl"
    :class="[
      ns.e('row'),
      ns.is('new', isNew),
      ns.is('checked', isChecked),
      ns.is('editing', isEditing),
    ]"
    :data-row-id="rowId"
    :style="rowStyle"
    @click.capture="onRowClickCapture"
    @dblclick.capture="onRowDblClickCapture"
    @contextmenu.capture="onRowContextMenuCapture"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div
      v-for="col in columns"
      :key="col.id"
      :class="[
        ns.e('cell'),
        ns.is('fixed-left', col.fixed === 'left'),
        ns.is('fixed-right', col.fixed === 'right'),
        ns.is('valid-error', hasCellError(col)),
        { 'fixed--hidden': isColumnHidden(col) },
      ]"
      :data-column-id="col.id"
      :style="bodyCellStyle(col)"
    >
      <span v-if="col.type === 'seq'">{{ seqLabel }}</span>
      <grid-cell
        v-else
        :row="row"
        :column="col"
        :editing="isEditing"
        :editable="isColEditable(col)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import { addUnit } from '@zoryn/utils'
import { hasPersistedRowId } from '../api/api-convention'
import {
  getColumnAlignStyle,
  isGridColumnEditable,
  resolveColumnMinWidth,
  resolveColumnWidth,
  resolveGridEditable,
} from '../column/meta'
import {
  getValidErrorKey,
  isRowEditing as checkRowEditing,
} from '../methods/edit'
import { getRowId } from '../utils/row-id'
import { isSameRow } from '../utils/is-same-row'
import { registerRowDom, unregisterRowDom } from '../utils/row-dom-registry'
import {
  clearHoverRowDom,
  HOVER_ROW_CLASS,
  setHoverRowDom,
} from '../utils/sync-hover-row-dom'
import { CURRENT_ROW_CLASS } from '../utils/sync-current-row-dom'
import { isFixedColumnHidden } from '../utils/fixed-column'
import { isGridInteractiveTarget } from '../utils/is-interactive-target'
import { canOpenGridContextMenu } from '../methods/row-context-menu'
import GridCell from './grid-cell.vue'
import type { CSSProperties } from 'vue'
import type { DataGrid, GridColumnInfo } from '../types'
import type { GridFixedType } from '../utils/fixed-column'

const props = defineProps<{
  row: Record<string, unknown>
  index: number
  virtualStart: number
  fixedType: GridFixedType
  overflowX: boolean
  columns: GridColumnInfo[]
  rowHeight: number
  tableWidth: number
  seqLabel: number
}>()

const emit = defineEmits<{
  click: [row: Record<string, unknown>]
  dblclick: [row: Record<string, unknown>]
}>()

const ns = useNamespace('grid')
const $grid = inject('$grid') as DataGrid
const { reactData, internalData, props: gridProps } = $grid
const rowEl = ref<HTMLElement | null>(null)

const rowId = computed(() => getRowId(props.row, props.index))
const isNew = computed(() => !hasPersistedRowId(props.row))
const isChecked = computed(
  () => $grid.isCheckedByCheckboxRow?.(props.row) ?? false
)
const isEditing = computed(() => checkRowEditing($grid, props.row))
const tableEditable = computed(() =>
  resolveGridEditable(gridProps, $grid.options)
)

const rowStyle = computed(
  () =>
    ({
      height: `${props.rowHeight}px`,
      top: `${props.virtualStart}px`,
      position: 'absolute',
      left: 0,
      minWidth: '100%',
      width: `${props.tableWidth}px`,
    }) as CSSProperties
)

function isColumnHidden(col: GridColumnInfo) {
  return isFixedColumnHidden(
    col,
    props.fixedType,
    props.overflowX,
    internalData.visibleColumns
  )
}

function bodyCellStyle(col: GridColumnInfo): CSSProperties {
  const w = resolveColumnWidth(col)
  return {
    width: addUnit(w),
    minWidth: addUnit(resolveColumnMinWidth(col)),
    flex: `0 0 ${addUnit(w)}`,
    ...getColumnAlignStyle(col.align),
  }
}

function hasCellError(col: GridColumnInfo) {
  const key = getValidErrorKey(rowId.value, col.id)
  return !!reactData.validErrorMaps[key]
}

function isColEditable(col: GridColumnInfo) {
  return isGridColumnEditable(col, tableEditable.value)
}

function onRowClickCapture(event: MouseEvent) {
  if (isGridInteractiveTarget(event.target)) {
    return
  }
  emit('click', props.row)
}

function onRowDblClickCapture(event: MouseEvent) {
  if (isGridInteractiveTarget(event.target)) {
    return
  }
  emit('dblclick', props.row)
}

function onRowContextMenuCapture(event: MouseEvent) {
  if (!canOpenGridContextMenu($grid, event)) {
    return
  }
  $grid.openContextMenu?.(event, props.row)
}

function onMouseEnter() {
  setHoverRowDom($grid, props.row)
}

function onMouseLeave() {
  clearHoverRowDom($grid)
}

onMounted(() => {
  if (rowEl.value) {
    registerRowDom(internalData, rowId.value, rowEl.value)
    if (isSameRow(reactData.currentRow, props.row)) {
      rowEl.value.classList.add(CURRENT_ROW_CLASS)
    }
    if (internalData.hoverRowId === rowId.value) {
      rowEl.value.classList.add(HOVER_ROW_CLASS)
    }
  }
})

onBeforeUnmount(() => {
  if (rowEl.value) {
    unregisterRowDom(internalData, rowId.value, rowEl.value)
  }
})
</script>
